import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  canAccessAbout,
  canAccessAdmins,
  canAccessDashboard,
  canAccessMessages,
  canAccessProducts,
  canAccessQuotations,
  canAccessSales,
  canAccessSettings,
  canAccessUsers,
  clearAdminSession,
  getAdminUser
} from "../utils/auth";

const drawerWidth = 280;

const AdminLayout = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sessionAlert, setSessionAlert] = useState("");
  const adminUser = getAdminUser();

  useEffect(() => {
    const handleSessionExpired = (event) => {
      const message = event.detail?.message || "Your login session has expired. Please sign in again.";
      setSessionAlert(message);

      window.setTimeout(() => {
        clearAdminSession();
        navigate("/login", { replace: true });
      }, 1200);
    };

    window.addEventListener("admin-session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("admin-session-expired", handleSessionExpired);
    };
  }, [navigate]);

  const navItems = useMemo(
    () =>
      [
        canAccessDashboard(adminUser?.role) && { label: "Dashboard", path: "/", icon: <DashboardRoundedIcon /> },
        { label: "Inventory", path: "/inventory", icon: <Inventory2RoundedIcon /> },
        canAccessProducts(adminUser?.role) && { label: "Categories", path: "/categories", icon: <CategoryRoundedIcon /> },
        canAccessProducts(adminUser?.role) && { label: "Products", path: "/products", icon: <Inventory2RoundedIcon /> },
        canAccessProducts(adminUser?.role) && { label: "Event Props", path: "/event-props", icon: <Inventory2RoundedIcon /> },
        canAccessQuotations(adminUser?.role) && {
          label: "Quotations",
          path: "/quotations",
          icon: <RequestQuoteRoundedIcon />
        },
        canAccessSales(adminUser?.role) && { label: "Sales", path: "/sales", icon: <PaidRoundedIcon /> },
        canAccessMessages(adminUser?.role) && { label: "Messages", path: "/messages", icon: <EmailRoundedIcon /> },
        canAccessAdmins(adminUser?.role) && { label: "Email Announcements", path: "/announcements", icon: <EmailRoundedIcon /> },
        canAccessUsers(adminUser?.role) && { label: "Users", path: "/users", icon: <PeopleAltRoundedIcon /> },
        canAccessAdmins(adminUser?.role) && {
          label: "Admins",
          path: "/admins",
          icon: <AdminPanelSettingsRoundedIcon />
        },
        canAccessAbout(adminUser?.role) && { label: "About Content", path: "/about", icon: <InfoRoundedIcon /> },
        canAccessSettings(adminUser?.role) && { label: "Settings", path: "/settings", icon: <SettingsRoundedIcon /> }
      ].filter(Boolean),
    [adminUser?.role]
  );

  const currentTitle = useMemo(
    () => navItems.find((item) => item.path === pathname)?.label || "Admin",
    [navItems, pathname]
  );

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #131826 0%, #1f2937 100%)",
        color: "#fff"
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.75 }}>
          Gentle Renters
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.72)", mb: 2 }}>
          Admin control room for listings, quotations, and client communication.
        </Typography>
        <Chip label="Admin Panel" sx={{ backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" }} />
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      <List sx={{ px: 2, py: 2 }}>
        {navItems.map((item) => {
          const active = pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              component={RouterLink}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                mb: 1,
                borderRadius: 3,
                backgroundColor: active ? "rgba(249, 115, 22, 0.18)" : "transparent",
                color: "#fff"
              }}
            >
              <ListItemIcon sx={{ color: "#fff", minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ mt: "auto", p: 2.5 }}>
        <Box sx={{ p: 2, borderRadius: 3, backgroundColor: "rgba(255,255,255,0.08)" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#ea580c" }}>A</Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{adminUser?.fullName || "Admin Workspace"}</Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.9rem" }}>
                {adminUser?.email || "Modern event rental operations"}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "background.default" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: "rgba(246,247,251,0.85)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(226, 232, 240, 0.8)"
        }}
      >
        <Toolbar sx={{ minHeight: 78 }}>
          <IconButton
            onClick={() => setMobileOpen((prev) => !prev)}
            sx={{ display: { md: "none" }, mr: 1 }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box>
            <Typography variant="h5" sx={pathname === "/announcements" ? { fontSize: { xs: "1.1rem", md: "1.5rem" } } : undefined}>{currentTitle}</Typography>
            <Typography sx={{ color: "text.secondary", ...(pathname === "/announcements" ? { display: { xs: "none", md: "block" } } : {}) }}>
              Keep the storefront, inquiries, and rental pipeline in sync.
            </Typography>
          </Box>

          <Box sx={{ ml: "auto" }}>
            <IconButton
              onClick={() => {
                clearAdminSession();
                navigate("/login", { replace: true });
              }}
            >
              <LogoutRoundedIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { width: drawerWidth, border: 0 }
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box", border: 0 }
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 4 }, mt: "78px" }}>
        {sessionAlert && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {sessionAlert}
          </Alert>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default AdminLayout;
